import {
  CheckCircleFilled,
  LeftOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import {
  BackBar,
  counted,
  dirhams,
  fullDate,
  IdTag,
  monthYear,
  parseAmount,
  type ResultOf,
  sumAmounts,
  toCents,
} from '@horof/shared';
import { Alert, App, Button, Drawer, Input, Result, Skeleton, Tag } from 'antd';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useClient, useMutation, useQuery } from 'urql';
import { graphql } from './gql';
import {
  bulkFailureMessage,
  paymentErrorMessage,
  type Receipt,
  whatsappReceiptsLink,
} from './receipts';

const MemberPaymentsDocument = graphql(`
  query MemberPayments($id: UUID!) {
    member(id: $id) {
      id
      fullName
      notifyPhone
      phoneStatus
      unpaidBills: bills(
        filter: { billStatus: { notEqualTo: PAID } }
        orderBy: [BILL_MONTH_ASC]
      ) {
        nodes {
          id
          billMonth
          billTotal
          paidAmount
          remainingAmount
          billStatus
          meter {
            label
          }
        }
      }
    }
    recentPayments: payments(
      filter: { bill: { memberId: { equalTo: $id } } }
      orderBy: [PAYMENT_DATE_DESC]
      first: 5
    ) {
      nodes {
        id
        receiptNo
        amount
        paymentDate
        receiptLink
        bill {
          billMonth
        }
      }
    }
    currentSettings {
      associationName
    }
  }
`);

const SavePaymentDocument = graphql(`
  mutation SavePayment($billId: UUID!, $amount: BigFloat!) {
    appSavePayment(input: { billId: $billId, amount: $amount }) {
      result {
        id
        receiptNo
        amount
        receiptLink
        bill {
          billMonth
        }
      }
    }
  }
`);

const BulkSavePaymentsDocument = graphql(`
  mutation BulkSavePayments($payments: JSON!) {
    appBulkSavePayments(input: { payments: $payments }) {
      result {
        paymentIds
        errors
        failedCount
      }
    }
  }
`);

const ReceiptsDocument = graphql(`
  query Receipts($ids: [UUID!]!) {
    payments(filter: { id: { in: $ids } }, orderBy: [RECEIPT_NO_ASC]) {
      nodes {
        id
        receiptNo
        amount
        receiptLink
        bill {
          billMonth
        }
      }
    }
  }
`);

type Member = NonNullable<ResultOf<typeof MemberPaymentsDocument>['member']>;
type UnpaidBill = NonNullable<Member['unpaidBills']['nodes'][number]>;
type Outcome = { receipts: Receipt[]; problems: string[] };

// Bulk mutations return no Bill/Payment, so tell urql what to refetch.
const refetchAfterPayments = { additionalTypenames: ['Payment', 'Bill'] };

const billsCount = (n: number) =>
  counted(n, {
    one: 'فاتورة واحدة',
    two: 'فاتورتين',
    few: 'فواتير',
    many: 'فاتورة',
    hundred: 'فاتورة',
  });

const toReceipt = (p: {
  id: string;
  receiptNo: string;
  amount: string;
  receiptLink: string;
  paymentDate?: string;
  bill: { billMonth: string } | null;
}): Receipt => ({
  id: p.id,
  receiptNo: p.receiptNo,
  amount: p.amount,
  receiptLink: p.receiptLink,
  billMonth: p.bill?.billMonth ?? null,
  paymentDate: p.paymentDate,
});

export function MemberPage() {
  const { memberId = '' } = useParams();
  const [{ data, error, fetching }, refetch] = useQuery({
    query: MemberPaymentsDocument,
    variables: { id: memberId },
    context: refetchAfterPayments,
  });
  const client = useClient();
  const { modal } = App.useApp();
  const [, savePayment] = useMutation(SavePaymentDocument);
  const [, bulkSave] = useMutation(BulkSavePaymentsDocument);

  const [saving, setSaving] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [payingBill, setPayingBill] = useState<UnpaidBill | null>(null);

  const member = data?.member;
  const bills = (member?.unpaidBills.nodes ?? []).filter(
    (b): b is UnpaidBill => b !== null,
  );
  const owed = sumAmounts(bills.map((b) => b.remainingAmount));

  if (!data) {
    return (
      <div className="page">
        <BackBar label="الأعضاء" />
        {error ? (
          <Alert type="error" showIcon message="تعذّر تحميل العضو" />
        ) : (
          <Skeleton active paragraph={{ rows: 6 }} />
        )}
      </div>
    );
  }
  if (!member) {
    return (
      <div className="page">
        <BackBar label="الأعضاء" />
        <Result status="404" title="العضو غير موجود" />
      </div>
    );
  }

  const done = (result: Outcome) => {
    setOutcome(result);
    setPayingBill(null);
    setSaving(false);
    window.scrollTo({ top: 0 });
  };

  const failed = (message: string) => {
    setProblem(message);
    setSaving(false);
    // Show what the server has now (a payment may have gone through).
    refetch({ requestPolicy: 'network-only' });
  };

  async function payOne(bill: UnpaidBill, amount: string) {
    setSaving(true);
    setProblem(null);
    setOutcome(null);
    const result = await savePayment(
      { billId: bill.id, amount },
      refetchAfterPayments,
    );
    const payment = result.data?.appSavePayment?.result;
    if (result.error || !payment) {
      failed(paymentErrorMessage(result.error));
      return;
    }
    done({ receipts: [toReceipt(payment)], problems: [] });
  }

  async function payAll() {
    const confirmed = await modal.confirm({
      title: 'تأكيد الاستلام',
      content: `استلام ${dirhams(owed)} من ${member?.fullName} عن ${billsCount(bills.length)}؟`,
      okText: 'نعم، استلمت المبلغ',
      cancelText: 'رجوع',
    });
    if (!confirmed) return;
    if (bills.length === 1) {
      await payOne(bills[0], bills[0].remainingAmount ?? '0');
      return;
    }

    setSaving(true);
    setProblem(null);
    setOutcome(null);
    // Each bill's remaining amount exactly as the API returned it.
    const payments = bills.map((b) => ({
      billId: b.id,
      amount: b.remainingAmount,
    }));
    const result = await bulkSave({ payments }, refetchAfterPayments);
    const bulk = result.data?.appBulkSavePayments?.result;
    if (result.error || !bulk) {
      failed(paymentErrorMessage(result.error));
      return;
    }
    // Not all-or-nothing: some bills may have failed while others were paid.
    const ids = (bulk.paymentIds ?? []).filter((id): id is string => !!id);
    const problems = (bulk.errors ?? []).map((e) => bulkFailureMessage(e));
    let receipts: Receipt[] = [];
    if (ids.length) {
      const saved = await client
        .query(ReceiptsDocument, { ids }, { requestPolicy: 'network-only' })
        .toPromise();
      receipts = (saved.data?.payments?.nodes ?? []).flatMap((p) =>
        p ? [toReceipt(p)] : [],
      );
    }
    done({ receipts, problems });
  }

  const canWhatsapp = member.phoneStatus === 'OK' && !!member.notifyPhone;
  const recent = (data.recentPayments?.nodes ?? []).flatMap((p) =>
    p ? [toReceipt(p)] : [],
  );

  return (
    <div className="page">
      <BackBar label="الأعضاء" />

      <section className="card">
        <h1 className="member-title">{member.fullName}</h1>
        <div className="owed-line">
          {bills.length ? (
            <>
              المبلغ المستحق: <strong>{dirhams(owed)}</strong> (
              {billsCount(bills.length)})
            </>
          ) : (
            <span className="clear-tag">لا توجد فواتير غير مؤداة</span>
          )}
        </div>
      </section>

      {outcome && (
        <section className="card outcome-card">
          {outcome.receipts.length > 0 && (
            <>
              <div className="outcome-title">
                <CheckCircleFilled /> تم تسجيل الأداء:{' '}
                {dirhams(sumAmounts(outcome.receipts.map((r) => r.amount)))}
              </div>
              <ReceiptList receipts={outcome.receipts} />
              {canWhatsapp && (
                <Button
                  size="large"
                  block
                  icon={<WhatsAppOutlined />}
                  className="whatsapp-button"
                  href={whatsappReceiptsLink(
                    member.notifyPhone ?? '',
                    outcome.receipts,
                    data.currentSettings?.associationName ?? 'الجمعية',
                  )}
                  target="_blank"
                >
                  إرسال الوصل عبر واتساب
                </Button>
              )}
            </>
          )}
          {outcome.problems.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message="بعض الفواتير لم يُسجَّل أداؤها"
              description={
                <ul className="problems">
                  {outcome.problems.map((p, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: messages may repeat
                    <li key={i}>{p}</li>
                  ))}
                </ul>
              }
            />
          )}
        </section>
      )}

      {problem && <Alert type="error" showIcon message={problem} />}

      {bills.length > 1 && (
        <Button
          type="primary"
          size="large"
          block
          className="big-button pay-all"
          loading={saving}
          onClick={payAll}
        >
          أداء الكل: {dirhams(owed)}
        </Button>
      )}

      {bills.map((bill) => (
        <BillCard
          key={bill.id}
          bill={bill}
          disabled={saving || fetching}
          onPay={() => {
            setProblem(null);
            setPayingBill(bill);
          }}
        />
      ))}

      {recent.length > 0 && (
        <section className="card">
          <div className="field-label">آخر الوصولات</div>
          <ReceiptList receipts={recent} />
        </section>
      )}

      <PayBillSheet
        bill={payingBill}
        saving={saving}
        onClose={() => setPayingBill(null)}
        onPay={payOne}
      />
      <IdTag id={member.id} label="معرّف العضو" />
    </div>
  );
}

function BillCard({
  bill,
  disabled,
  onPay,
}: {
  bill: UnpaidBill;
  disabled: boolean;
  onPay: () => void;
}) {
  const partlyPaid = toCents(bill.paidAmount ?? '0') > 0;
  return (
    <section className="card bill-card">
      <div className="bill-head">
        <Link to={`/bill/${bill.id}`} className="bill-month">
          {monthYear(bill.billMonth)} <LeftOutlined className="row-arrow" />
        </Link>
        {bill.billStatus === 'OVERDUE' ? (
          <Tag color="red">متأخرة</Tag>
        ) : (
          <Tag color="orange">غير مؤداة</Tag>
        )}
      </div>
      {bill.meter?.label && (
        <div className="field-hint">العداد {bill.meter.label}</div>
      )}
      <dl className="bill-values">
        <dt>مبلغ الفاتورة</dt>
        <dd>{dirhams(bill.billTotal)}</dd>
        {partlyPaid && (
          <>
            <dt>المؤدّى</dt>
            <dd>{dirhams(bill.paidAmount)}</dd>
          </>
        )}
        <dt>الباقي</dt>
        <dd className="remaining">{dirhams(bill.remainingAmount)}</dd>
      </dl>
      <Button size="large" block disabled={disabled} onClick={onPay}>
        أداء هذه الفاتورة
      </Button>
    </section>
  );
}

function PayBillSheet({
  bill,
  saving,
  onClose,
  onPay,
}: {
  bill: UnpaidBill | null;
  saving: boolean;
  onClose: () => void;
  onPay: (bill: UnpaidBill, amount: string) => void;
}) {
  const [value, setValue] = useState('');
  const remaining = bill?.remainingAmount ?? '0';
  const amount = parseAmount(value);
  const tooMuch = amount !== null && toCents(amount) > toCents(remaining);
  const problem =
    value === ''
      ? null
      : amount === null
        ? 'اكتب مبلغاً صحيحاً، مثلاً 45 أو 45,50'
        : tooMuch
          ? `المبلغ أكبر من الباقي (${dirhams(remaining)})`
          : null;

  return (
    <Drawer
      open={bill !== null}
      placement="bottom"
      height="auto"
      onClose={onClose}
      title={bill ? `فاتورة ${monthYear(bill.billMonth)}` : ''}
      afterOpenChange={(open) => {
        if (open) setValue(remaining);
      }}
      destroyOnHidden
    >
      <div className="sheet">
        <div className="field-hint">الباقي: {dirhams(remaining)}</div>
        <label className="field-label" htmlFor="amount">
          المبلغ المستلم (درهم)
        </label>
        <Input
          id="amount"
          className="amount-input ltr-num"
          inputMode="decimal"
          autoComplete="off"
          value={value}
          status={problem ? 'error' : undefined}
          onChange={(e) => setValue(e.target.value)}
          // Prefilled with the balance: typing a partial amount replaces it.
          onFocus={(e) => {
            const input = e.target;
            setTimeout(() => input.select(), 0);
          }}
        />
        {problem && <div className="field-error">{problem}</div>}
        {amount && !tooMuch && toCents(amount) < toCents(remaining) && (
          <div className="field-hint">أداء جزئي: يبقى جزء من الفاتورة.</div>
        )}
        <Button
          type="primary"
          size="large"
          block
          className="big-button"
          loading={saving}
          disabled={!amount || tooMuch}
          onClick={() => bill && amount && onPay(bill, amount)}
        >
          {amount && !tooMuch ? `تأكيد استلام ${dirhams(amount)}` : 'تأكيد'}
        </Button>
      </div>
    </Drawer>
  );
}

function ReceiptList({ receipts }: { receipts: Receipt[] }) {
  return (
    <ul className="receipt-list">
      {receipts.map((r) => (
        <li key={r.id}>
          <span className="receipt-main">
            <span className="receipt-no">
              وصل <span className="ltr-num">{r.receiptNo}</span>
            </span>
            <span className="field-hint">
              {dirhams(r.amount)}
              {r.billMonth ? ` · فاتورة ${monthYear(r.billMonth)}` : ''}
              {r.paymentDate ? ` · ${fullDate(r.paymentDate)}` : ''}
            </span>
          </span>
          <Button href={r.receiptLink} target="_blank">
            فتح الوصل
          </Button>
        </li>
      ))}
    </ul>
  );
}
